import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme, App as AntdApp } from 'antd';
import MainLayout from './components/layout/MainLayout';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Categories from './pages/Categories';
import Locations from './pages/Locations';
import Orders from './pages/Orders';
import Customers from './pages/Customers';
import Quotes from './pages/Quotes';
import PurchaseOrders from './pages/PurchaseOrders';
import Vendors from './pages/Vendors';
import Receiving from './pages/Receiving';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Automation from './pages/Automation';
import Notifications from './pages/Notifications';
import Profile from './pages/Profile';
import Search from './pages/Search';
import BarcodeScanner from './pages/BarcodeScanner';
import EWayBills from './pages/EWayBills';
import Stock from './pages/Stock';
import ErrorBoundary from './components/common/ErrorBoundary';
import AIChatbot from './components/common/AIChatbot';
import useStore from './store/useStore';
// import useStore from './store/useStore'; // Unused for now
import './index.css';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Logout from './pages/Auth/Logout';
import { authApi } from './services/authApi';
import SuperadminLogin from './pages/Superadmin/Login';
import SuperadminDashboard from './pages/Superadmin/Dashboard';
import SuperadminLayout from './components/superadmin/SuperadminLayout';
import SuperadminUsers from './pages/Superadmin/Users';
import SuperadminCompanies from './pages/Superadmin/Companies';

const App = () => {
  // Authentication flow with proper backend verification

  const { themeMode, isAuthenticated, setUser, logout } = useStore();
  const [authLoading, setAuthLoading] = useState(true);
  const isDark = themeMode === 'dark';

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDark]);

  // Bootstrap auth on load: verify backend authentication
  useEffect(() => {
    (async () => {
      try {
        setAuthLoading(true);
        const me = await authApi.me();
        if (me?.email) {
          setUser(me);
        } else {
          logout(); // Clear any stale frontend auth state
        }
      } catch (error) {
        // Backend authentication failed - clear any stale auth state
        logout();
      } finally {
        setAuthLoading(false);
      }
    })();
  }, [setUser, logout]);

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  const theme = {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: '#0ea5e9',
      colorSuccess: '#10b981',
      colorWarning: '#f59e0b',
      colorError: '#ef4444',
      colorInfo: '#3b82f6',
      borderRadius: 6,
      wireframe: false,
      ...(isDark
        ? {
            // Bluish dark greys
            colorBgBase: '#0f172a', // slate-900
            colorBgContainer: '#0b1220', // bluish dark
            colorBorder: '#334155', // slate-700
            colorSplit: '#1f2937',
            // Readable text
            colorText: '#e5e7eb', // gray-200
            colorTextSecondary: '#94a3b8', // slate-400
          }
        : {})
    },
    components: {
      Layout: {
        headerBg: isDark ? undefined : '#ffffff',
        siderBg: isDark ? undefined : '#ffffff',
        bodyBg: isDark ? undefined : '#f8fafc',
      },
      Menu: {
        itemBg: 'transparent',
        ...(isDark
          ? {
              // Bluish selection/hover in dark
              itemSelectedBg: '#0b2942',
              itemSelectedColor: '#e5f2ff',
              itemHoverBg: '#0a2236',
              itemColor: '#cbd5e1',
            }
          : {
              itemSelectedBg: '#e0f2fe',
              itemSelectedColor: '#0369a1',
              itemHoverBg: '#f1f5f9',
            }),
      },
      Button: {
        borderRadius: 6,
      },
      Card: {
        borderRadius: 8,
      },
      Table: {
        borderRadius: 8,
      },
      Typography: isDark
        ? {
            colorTextHeading: '#e2e8f0',
            colorTextDescription: '#94a3b8',
          }
        : undefined,
    },
  };

  // Use real auth state from store

  return (
    <ErrorBoundary>
      <ConfigProvider theme={theme}>
        <AntdApp>
          <Router>
          <div className="App">
            <Routes>
              {isAuthenticated ? (
                // Authenticated user routes
                <>
                  <Route path="/superadmin" element={<SuperadminLayout />}>
                    <Route index element={<SuperadminDashboard />} />
                    <Route path="users" element={<SuperadminUsers />} />
                    <Route path="companies" element={<SuperadminCompanies />} />
                  </Route>
                  <Route path="/" element={<MainLayout />}>
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="products" element={<Products />} />
                    <Route path="categories" element={<Categories />} />
                    <Route path="locations" element={<Locations />} />
                    <Route path="barcode" element={<BarcodeScanner />} />
                    <Route path="stock" element={<Stock />} />
                    <Route path="e-waybills" element={<EWayBills />} />
                    <Route path="orders" element={<Orders />} />
                    <Route path="customers" element={<Customers />} />
                    <Route path="quotes" element={<Quotes />} />
                    <Route path="eway-bills" element={<EWayBills />} />
                    <Route path="purchase-orders" element={<PurchaseOrders />} />
                    <Route path="vendors" element={<Vendors />} />
                    <Route path="receiving" element={<Receiving />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="automation" element={<Automation />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="search" element={<Search />} />
                    <Route path="logout" element={<Logout />} />
                  </Route>
                  {/* Redirect any other authenticated routes to dashboard */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </>
              ) : (
                // Unauthenticated user routes
                <>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/superadmin/login" element={<SuperadminLogin />} />
                  {/* Redirect any other unauthenticated routes to login */}
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </>
              )}
            </Routes>
            {/* AI Chatbot - Available on all pages when authenticated */}
            {isAuthenticated && <AIChatbot />}
          </div>
          </Router>
        </AntdApp>
      </ConfigProvider>
    </ErrorBoundary>
  );
};

export default App;
